/*
 * Jira Ticket:
 * Created Date: Thu, 4th Jun 2020, 11:28:18 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import {
  ArrayInput,
  BooleanInput,
  Create,
  FormDataConsumer,
  NumberInput,
  required,
  SimpleForm,
  TextInput,
  usePermissions,
  Title,
  AutocompleteArrayInput,
  AutocompleteInput,
  useDataProvider,
  fetchStart,
  fetchEnd,
} from 'react-admin';
import { InputAdornment, Typography } from '@material-ui/core';
import { CustomSimpleFormIterator } from 'Components';
import { AccessDenied } from 'Layout';
import { isSuperAdmin } from 'utils/roles';
import { isValidCost, nonNegativeInt } from 'utils/validation';
import { columnStyles } from 'styles';
import { useDispatch } from 'react-redux';
import * as R from 'ramda';

const costValidation = [required(), isValidCost];
const nonNegativeValidation = [required(), nonNegativeInt];

const SanitizedForm = ({
  classes,
  capabilities,
  resource,
  organisationChoices,
  ...props
}) => {
  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="name"
          label="resources.SubscriptionTier.fields.name"
          validate={required()}
        />
        <BooleanInput
          label="resources.SubscriptionTier.fields.isFreeTier"
          source="isFreeTier"
        />
        <FormDataConsumer>
          {({ formData }) =>
            !formData.isFreeTier ? (
              <NumberInput
                source="monthlyPlan.cost.amount"
                validate={costValidation}
                parse={x => x} // prevent overwriting 12.00 -> 12
                label="resources.SubscriptionTier.fields.monthlyCost"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
              />
            ) : null
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) =>
            !formData.isFreeTier ? (
              <NumberInput
                source="annualPlan.cost.amount"
                validate={costValidation}
                parse={x => x} // prevent overwriting 12.00 -> 12
                label="resources.SubscriptionTier.fields.annualCost"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
              />
            ) : null
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) =>
            !formData.isFreeTier ? (
              <NumberInput
                source="freeTrialDuration"
                label="resources.SubscriptionTier.fields.freeTrailLength"
                step={1}
                validate={nonNegativeValidation}
              />
            ) : null
          }
        </FormDataConsumer>

        <NumberInput
          source="maximumEmployees"
          label="resources.SubscriptionTier.fields.maxEmployee"
          step={1}
          validate={nonNegativeValidation}
        />
        <NumberInput
          source="maximumSubcontractors"
          label="resources.SubscriptionTier.fields.maxSubcontractor"
          step={1}
          validate={nonNegativeValidation}
        />
        <NumberInput
          source="maximumProjects"
          label="resources.SubscriptionTier.fields.maxProject"
          step={1}
          validate={nonNegativeValidation}
        />
        <BooleanInput
          label="resources.SubscriptionTier.fields.isCustomTier"
          source="isCustomTier"
        />
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            const { isCustomTier } = formData;
            return isCustomTier ? (
              <AutocompleteArrayInput
                source="customOrganisationsIds"
                label="resources.SubscriptionTier.fields.customOrganisations"
                choices={organisationChoices}
                // suggestionLimit={20}
              />
            ) : null;
          }}
        </FormDataConsumer>
      </div>
      <div className={classes.permissionColumn}>
        <Typography variant="h6" gutterBottom>
          Permissions
        </Typography>
        <ArrayInput source="capabilities" label="">
          <CustomSimpleFormIterator disableAdd disableRemove>
            <FormDataConsumer>
              {({ getSource, scopedFormData }) => {
                return (
                  <BooleanInput
                    key={scopedFormData.id}
                    source={getSource('enabled')}
                    label={scopedFormData.description}
                  />
                );
              }}
            </FormDataConsumer>
          </CustomSimpleFormIterator>
        </ArrayInput>
      </div>
    </div>
  );
};

const SubscriptionTierCreate = props => {
  const classes = columnStyles();
  const { permissions } = usePermissions();

  const dataProvider = useDataProvider();
  const dispatch = useDispatch();
  const [organisationChoices, setOrganisationChoices] = useState([]);

  const useMountEffect = fun => useEffect(fun, []);
  const checkHasOrganisations = () => {
    dispatch(fetchStart());
    dataProvider('GET_LIST', 'Organisation', {
      pagination: { page: 1, perPage: 1000 },
    })
      .then(result => {
        const orgs = R.path(['data'], result) || [];
        const choices = orgs.map(org => {
          return { id: org.id, name: org.name };
        });
        setOrganisationChoices(choices);
      })
      .catch()
      .finally(() => {
        dispatch(fetchEnd());
      });

    return () => {};
  };
  useMountEffect(checkHasOrganisations); // run once

  return isSuperAdmin(permissions) ? (
    <>
      <Title title="resources.General.cmsTitle" />
      <Create title="resources.SubscriptionTier.fields.createTitle" {...props}>
        <SimpleForm>
          <SanitizedForm
            classes={classes}
            organisationChoices={organisationChoices}
          />
        </SimpleForm>
      </Create>
    </>
  ) : (
    <AccessDenied />
  );
};

export default SubscriptionTierCreate;
